export const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

export const ACTIVITY_OPTIONS = [
  { label: '1-2', value: '1-2 days per week' },
  { label: '3-4', value: '3-4 days per week' },
  { label: '5-6', value: '5-6 days per week' },
  { label: '7', value: '7 days per week' },
];

export const ACTIVITY_TARGET_OPTIONS = {
  '1-2 days per week': [1, 2],
  '3-4 days per week': [3, 4],
  '5-6 days per week': [5, 6],
  '7 days per week': [7],
};

export const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Mon', fullLabel: 'Monday' },
  { value: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
  { value: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
  { value: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
  { value: 'friday', label: 'Fri', fullLabel: 'Friday' },
  { value: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
  { value: 'sunday', label: 'Sun', fullLabel: 'Sunday' },
];

export const DIETARY_OPTIONS = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Low-Carb', 'Keto'];
export const WORKOUT_OPTIONS = ['Strength Training', 'Cardio', 'Yoga', 'Pilates', 'HIIT', 'Cycling', 'Running', 'Meditation'];
export const EQUIPMENT_CHIPS = ['None', 'Dumbbells', 'Resistance bands', 'Kettlebell', 'Pull-up bar', 'Gym access'];

export const UNIT_OPTIONS = [
  { value: 'metric', label: 'Metric (kg, km)' },
  { value: 'imperial', label: 'Imperial (lbs, miles)' },
];

export const initialProfileFormState = {
  personalInformation: {
    fullName: '',
    age: '',
    sex: '',
    location: '',
    heightCm: '',
    weightKg: '',
  },
  healthAndFitnessDetails: {
    exerciseFamiliarity: 'Comfortable',
    activityFrequency: '',
    weeklyWorkoutTarget: '',
    preferredWorkoutDays: [],
    fitnessGoals: '',
    workoutPreferences: [],
  },
  equipmentAndPreferences: {
    preferredUnits: 'metric',
    availableEquipment: [],
  },
  additionalInformation: {
    medicalConditions: '',
    dietaryPreferences: [],
    allergies: 'None',
  },
  bodyComposition: {
    neckCm: '',
    chestCm: '',
    waistCm: '',
    hipsCm: '',
    thighCm: '',
    notes: '',
  },
};

export const ensureArray = (value) => (Array.isArray(value) ? value : []);

export const getWorkoutTargetOptions = (activityFrequency) =>
  ACTIVITY_TARGET_OPTIONS[activityFrequency] || [];

export const normalizeWeeklyWorkoutTarget = (value, activityFrequency) => {
  const targetOptions = getWorkoutTargetOptions(activityFrequency);
  const numeric = Number(value);
  if (targetOptions.includes(numeric)) {
    return numeric;
  }
  return targetOptions[targetOptions.length - 1] || '';
};

export const normalizePreferredWorkoutDays = (days = []) => {
  const validDays = new Set(WEEKDAY_OPTIONS.map((option) => option.value));
  return ensureArray(days).filter((day, index, list) =>
    validDays.has(day) && list.indexOf(day) === index
  );
};

export const toggleListValue = (list, value) => {
  const current = ensureArray(list);
  if (current.includes(value)) {
    return current.filter((item) => item !== value);
  }
  if (value === 'None') {
    return ['None'];
  }
  return [...current.filter((item) => item !== 'None'), value];
};

export const mapLegacyFamiliarity = (profile) => {
  const explicit = profile?.healthAndFitnessDetails?.exerciseFamiliarity;
  if (explicit) {
    return explicit;
  }
  const legacy = profile?.healthAndFitnessDetails?.fitnessLevel;
  if (legacy === 'Beginner') return 'Novice';
  if (legacy === 'Advanced') return 'Advanced';
  return 'Comfortable';
};

export const hydrateProfile = (profile) => {
  const next = {
    personalInformation: { ...initialProfileFormState.personalInformation },
    healthAndFitnessDetails: { ...initialProfileFormState.healthAndFitnessDetails },
    equipmentAndPreferences: { ...initialProfileFormState.equipmentAndPreferences },
    additionalInformation: { ...initialProfileFormState.additionalInformation },
    bodyComposition: { ...initialProfileFormState.bodyComposition },
  };

  if (!profile || typeof profile !== 'object') {
    return next;
  }

  if (profile.personalInformation && typeof profile.personalInformation === 'object') {
    next.personalInformation = {
      ...next.personalInformation,
      ...profile.personalInformation,
      heightCm: profile.personalInformation.heightCm?.toString() || '',
      weightKg: profile.personalInformation.weightKg?.toString() || '',
    };
  }

  if (profile.healthAndFitnessDetails && typeof profile.healthAndFitnessDetails === 'object') {
    next.healthAndFitnessDetails = {
      ...next.healthAndFitnessDetails,
      ...profile.healthAndFitnessDetails,
      exerciseFamiliarity: mapLegacyFamiliarity(profile),
    };
  }
  next.healthAndFitnessDetails.workoutPreferences = ensureArray(next.healthAndFitnessDetails.workoutPreferences);
  next.healthAndFitnessDetails.weeklyWorkoutTarget = normalizeWeeklyWorkoutTarget(
    next.healthAndFitnessDetails.weeklyWorkoutTarget,
    next.healthAndFitnessDetails.activityFrequency
  );
  next.healthAndFitnessDetails.preferredWorkoutDays = normalizePreferredWorkoutDays(
    next.healthAndFitnessDetails.preferredWorkoutDays
  ).slice(0, Number(next.healthAndFitnessDetails.weeklyWorkoutTarget) || 0);

  if (profile.equipmentAndPreferences && typeof profile.equipmentAndPreferences === 'object') {
    next.equipmentAndPreferences = {
      ...next.equipmentAndPreferences,
      ...profile.equipmentAndPreferences,
    };
  }
  next.equipmentAndPreferences.availableEquipment = ensureArray(next.equipmentAndPreferences.availableEquipment);
  next.equipmentAndPreferences.preferredUnits = next.equipmentAndPreferences.preferredUnits || 'metric';

  if (profile.additionalInformation && typeof profile.additionalInformation === 'object') {
    next.additionalInformation = {
      ...next.additionalInformation,
      ...profile.additionalInformation,
    };
  }
  next.additionalInformation.dietaryPreferences = ensureArray(next.additionalInformation.dietaryPreferences);
  next.additionalInformation.allergies = next.additionalInformation.allergies || 'None';

  if (profile.bodyComposition && typeof profile.bodyComposition === 'object') {
    next.bodyComposition = {
      ...next.bodyComposition,
      ...profile.bodyComposition,
      neckCm: profile.bodyComposition.neckCm?.toString() || '',
      chestCm: profile.bodyComposition.chestCm?.toString() || '',
      waistCm: profile.bodyComposition.waistCm?.toString() || '',
      hipsCm: profile.bodyComposition.hipsCm?.toString() || '',
      thighCm: profile.bodyComposition.thighCm?.toString() || '',
      notes: profile.bodyComposition.notes?.toString() || '',
    };
  }

  return next;
};

export const parseNumericInput = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = value.toString().trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const trimTrailingZeros = (value, decimals) => {
  const fixed = value.toFixed(decimals);
  return decimals === 0 ? fixed : fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
};

export const convertLengthInputToMetric = (raw, preferredUnits = 'metric') => {
  const parsed = parseNumericInput(raw);
  if (parsed === null) return '';
  const metric = parsed * (preferredUnits === 'imperial' ? 2.54 : 1);
  return Number(metric.toFixed(2)).toString();
};

export const convertWeightInputToMetric = (raw, preferredUnits = 'metric') => {
  const parsed = parseNumericInput(raw);
  if (parsed === null) return '';
  const metric = parsed * (preferredUnits === 'imperial' ? 0.45359237 : 1);
  return Number(metric.toFixed(2)).toString();
};

export const getLengthDisplayValue = (stored, preferredUnits = 'metric', decimalsImperial = 1, decimalsMetric = 0) => {
  if (!stored) return '';
  const parsed = Number(stored);
  if (!Number.isFinite(parsed)) return '';
  return preferredUnits === 'imperial'
    ? trimTrailingZeros(parsed / 2.54, decimalsImperial)
    : trimTrailingZeros(parsed, decimalsMetric);
};

export const getWeightDisplayValue = (stored, preferredUnits = 'metric') => {
  if (!stored) return '';
  const parsed = Number(stored);
  if (!Number.isFinite(parsed)) return '';
  return preferredUnits === 'imperial'
    ? trimTrailingZeros(parsed * 2.20462262, 1)
    : trimTrailingZeros(parsed, 0);
};

export const buildOnboardingProfilePayload = (formState, { hasAllergies = false } = {}) => ({
  personalInformation: {
    fullName: formState.personalInformation.fullName?.trim() || '',
    age: formState.personalInformation.age?.toString().trim() || '',
    sex: formState.personalInformation.sex || '',
    location: formState.personalInformation.location?.trim() || '',
    heightCm: formState.personalInformation.heightCm?.toString().trim() || '',
    weightKg: formState.personalInformation.weightKg?.toString().trim() || '',
  },
  healthAndFitnessDetails: {
    exerciseFamiliarity: formState.healthAndFitnessDetails.exerciseFamiliarity || 'Comfortable',
    activityFrequency: formState.healthAndFitnessDetails.activityFrequency || '',
    weeklyWorkoutTarget: normalizeWeeklyWorkoutTarget(
      formState.healthAndFitnessDetails.weeklyWorkoutTarget,
      formState.healthAndFitnessDetails.activityFrequency
    ),
    preferredWorkoutDays: normalizePreferredWorkoutDays(formState.healthAndFitnessDetails.preferredWorkoutDays),
    fitnessGoals: formState.healthAndFitnessDetails.fitnessGoals?.trim() || '',
    workoutPreferences: ensureArray(formState.healthAndFitnessDetails.workoutPreferences),
  },
  equipmentAndPreferences: {
    preferredUnits: formState.equipmentAndPreferences.preferredUnits || 'metric',
    availableEquipment: ensureArray(formState.equipmentAndPreferences.availableEquipment),
  },
  additionalInformation: {
    medicalConditions: formState.additionalInformation.medicalConditions?.trim() || '',
    dietaryPreferences: ensureArray(formState.additionalInformation.dietaryPreferences),
    allergies: hasAllergies
      ? formState.additionalInformation.allergies?.trim() || 'Not specified'
      : 'None',
  },
  bodyComposition: {
    neckCm: formState.bodyComposition.neckCm?.toString().trim() || '',
    chestCm: formState.bodyComposition.chestCm?.toString().trim() || '',
    waistCm: formState.bodyComposition.waistCm?.toString().trim() || '',
    hipsCm: formState.bodyComposition.hipsCm?.toString().trim() || '',
    thighCm: formState.bodyComposition.thighCm?.toString().trim() || '',
    notes: formState.bodyComposition.notes?.trim() || '',
  },
});

export const validateWorkoutDaySelection = (formState) => {
  const details = formState?.healthAndFitnessDetails || initialProfileFormState.healthAndFitnessDetails;
  const { activityFrequency, weeklyWorkoutTarget, preferredWorkoutDays } = details;
  if (!activityFrequency) {
    return { valid: true, expected: 0, actual: 0 };
  }
  const expected = Number(normalizeWeeklyWorkoutTarget(weeklyWorkoutTarget, activityFrequency)) || 0;
  const actual = normalizePreferredWorkoutDays(preferredWorkoutDays).length;
  return {
    valid: expected === 0 || actual === expected,
    expected,
    actual,
  };
};
